import React, { useEffect, useState, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Modal from "@material-ui/core/Modal";
import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Switch,
  TextField,
} from "@material-ui/core";
import MainContext from "./context/main/mainContext";
import ConfirmationModal from "./components/ConfrimationModal";
import useCommonMemberAndTeamAction from "./hooks/useCommonMemberAndTeamActions";
import IntegrationsSection from "./components/IntegrationsSection";
import MarketingEmailSettings from "./team/components/MarketingEmailSettings";

function getModalStyle() {
  return {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: "65%",
    display: "block",
    "overflow-y": "auto",
    "max-height": "80%",
  };
}

const useStyles = makeStyles((theme) => ({
  paper: {
    position: "absolute",
    width: 400,
    backgroundColor: theme.palette.background.paper,
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
}));

export default function UserModal(props) {
  const mainContext = useContext(MainContext);
  const {
    user,
    getMemberById,
    setMemberAdmin,
    setEmailVerified,
    member,
    setPostFlowEmail,
    changeTab,
    getLoginToken,
    isProd,
    updateLinksData,
    getMemberIntegrationConnections,
    integrationConnections,
    updateMembersTeams,
    registerDevice,
    getDevicesForMember,
    memberDevices,
    getLinksData,
    linksData,
    getMemberCrmExportLogs,
    crmExportLogs,
    updateMemberLinksData,
    deleteMember,
  } = mainContext;

  const [adminControlsExpanded, setAdminControlsExpanded] = useState(false);
  const [integrationsExpanded, setIntegrationsExpanded] = useState(false);
  const [isIntegrationsLoading, setIsIntegrationsLoading] = useState(false);
  const [isCrmExportLogsLoading, setIsCrmExportLogsLoading] = useState(false);
  const [deviceFormValues, setDeviceFormValues] = useState({
    id: "",
    nickname: "",
  });
  const [linksDataFormValues, setLinksDataFormValues] = useState({
    exchangeText: "",
    reassignMemberId: "",
  });

  const {
    isTextFollowUp,
    isEmailFollowUp,
    isLcfEnabled,
    email,
    editEmailOpen,
    handleSetEmailOpen,
    handleEmailChange,
    isCommonActionLoading,
    handleUpdateMember,
  } = useCommonMemberAndTeamAction("member");

  const classes = useStyles();
  const [modalStyle] = React.useState(getModalStyle);

  const [mainTeam, setMainTeam] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [newTeamId, setNewTeamId] = useState("");

  const handleTeamTransferSubmit = async (e) => {
    e.preventDefault();
    const confirmTeamTransfer = window.confirm(
      "Are you sure you want to transfer this member to a new team? This can have unintended consequences.",
    );

    if (!confirmTeamTransfer) return;

    if (newTeamId && mainTeam?.id) {
      setIsLoading(true);
      try {
        let result;

        try {
          await updateMembersTeams(mainTeam?.id, { teamId: Number(newTeamId) });
          await handleUpdateMember({ activeTeamsMember: 1 });
        } catch (error) {
          console.error(error);
          setIsLoading(false);
        }

        result.forEach((item) => {
          if (item.error) {
            throw new Error(item.error);
          }
        });
      } catch (error) {
        console.error(error);
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    setIsLoading(true);

    (async () => {
      await Promise.all([
        getMemberById(props.userId, (err) => {
          if (!props.userId) return;
          if (err) {
            alert(err);
          }
        }),
        getDevicesForMember(props.userId, () => {}),
        getLinksData(props.userId, () => {}),
      ]);

      setIsLoading(false);
    })();
  }, [props.userId]);

  useEffect(() => {
    if (mainTeam?.team?.id) {
      setNewTeamId(mainTeam.team.id);
    }
  }, [mainTeam]);

  const makeAdmin = (userId, teamId, isAdmin) => {
    setIsLoading(true);
    setMemberAdmin(userId, teamId, isAdmin, (err) => {
      if (err) {
        alert(err);
      }
      setIsLoading(false);
    });
  };

  const changeEmailVerified = (userId, emailVerified) => {
    if (isLoading) return;

    setIsLoading(true);
    setEmailVerified(userId, emailVerified, (err) => {
      if (err) {
        alert(err);
      }
      setIsLoading(false);
    });
  };

  const startTeamsTrial = (email) => {
    setPostFlowEmail(email);
    changeTab(3);
  };

  const adminLogin = (uid, isStaging) => {
    setIsLoading(true);
    getLoginToken(uid, (err, token) => {
      if (err) {
        alert(err);
      }

      let baseUrl = "https://admin.popl.co/";
      if (!isProd) {
        baseUrl = "https://dev-teams.popl.co/";
      }
      if (isStaging) {
        baseUrl = "https://staging-teams.popl.co/";
      }
      const url = new URL(`${baseUrl}sign-in`);
      url.searchParams.append("addon", token);
      url.searchParams.append("poplAdminUser", user.email);
      console.log(url.href);
      window.open(url.href, "_blank");
      setIsLoading(false);
    });
  };

  const handleClose = () => {
    props.closeModal();
    handleSetEmailOpen(false);
  };

  useEffect(() => {
    setMainTeam(
      member?.member?.memberTeams
        ? member?.member?.memberTeams?.find((memberTeam) => memberTeam.team.parent === null)
        : null,
    );
  }, [member]);

  useEffect(() => {
    if (linksData && linksData.length > 0 && member?.member?.id) {
      const initialValues = {};
      linksData.forEach((item) => {
        initialValues[item.id] = {
          exchangeText: item.exchangeText || "",
          reassignMemberId: member?.member?.id || "",
        };
      });
      setLinksDataFormValues(initialValues);
    }
  }, [linksData, member?.member?.id]);

  const handleFormChange = (e, linkDataId) => {
    const { name, value } = e.target;
    setLinksDataFormValues({
      ...linksDataFormValues,
      [linkDataId]: {
        ...linksDataFormValues[linkDataId],
        [name]: value,
      },
    });
  };

  const handleFormSubmitLinksData = async (e, linkDataId) => {
    e.preventDefault();

    if (linkDataId) {
      const formValues = linksDataFormValues[linkDataId];
      const updateParams = {};

      if (formValues?.exchangeText) {
        updateParams.exchangeText = formValues.exchangeText;
      }

      if (formValues?.reassignMemberId) {
        updateParams.mid = Number.parseInt(formValues.reassignMemberId, 10);
      }

      setIsLoading(true);
      await updateLinksData(linkDataId, updateParams);
      setIsLoading(false);
    }
  };

  const handleDeviceFormChange = (e) => {
    const { name, value } = e.target;
    setDeviceFormValues({
      ...deviceFormValues,
      [name]: value,
    });
  };

  const handleToggleAdminControls = () => {
    setAdminControlsExpanded(!adminControlsExpanded);
  };

  const handleToggleIntegrations = async () => {
    const newExpandedState = !integrationsExpanded;
    setIntegrationsExpanded(newExpandedState);

    if (newExpandedState) {
      setIsIntegrationsLoading(true);
      await Promise.all([
        getMemberIntegrationConnections(member?.member?.id, () => {
          setIsIntegrationsLoading(false);
        }),
        getMemberCrmExportLogs(member?.member?.id, "", () => {
          setIsCrmExportLogsLoading(false);
        }),
      ]);
    }
  };

  const handleSearchCrmExportLogs = async (memberId, searchQuery) => {
    setIsCrmExportLogsLoading(true);
    await getMemberCrmExportLogs(memberId, searchQuery, () => {
      setIsCrmExportLogsLoading(false);
    });
  };

  const handleRegisterDevice = async (e) => {
    e.preventDefault();

    if (!deviceFormValues.id) {
      return;
    }

    setIsLoading(true);

    try {
      await registerDevice({
        name: deviceFormValues.id,
        nickname: deviceFormValues.nickname,
        mid: member?.member?.id,
      });
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const body = (
    <div>
      <h2 id="simple-modal-title">Member</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        {user.can_access_admin && (
          <div
            className="buttonContainer"
            style={{ margin: 0 }}
            onClick={(e) => adminLogin(member?.member?.uid)}
          >
            Admin Login
          </div>
        )}
        {user.can_access_admin && isProd && (
          <div
            className="buttonContainer"
            style={{ margin: 0 }}
            onClick={(e) => adminLogin(member?.member?.uid, true)}
          >
            Staging Login
          </div>
        )}
      </div>

      <div
        style={{
          border: "1px solid gray",
          margin: "1rem 0",
          padding: "1rem",
        }}
      >
        <div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              {editEmailOpen ? (
                <TextField
                  required="true"
                  variant="outlined"
                  value={email}
                  type="text"
                  style={{ width: 400 }}
                  label={"Email"}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  disabled={isCommonActionLoading}
                />
              ) : (
                <p id="simple-modal-description">Email: {email}</p>
              )}
              <p>ID: {member?.member?.id}</p>
              <p>Firebase ID: {member?.member?.uid}</p>
              <div className="checkboxDiv">
                <Checkbox
                  color="black"
                  checked={member?.member?.emailVerified}
                  size="small"
                  onChange={(e) => changeEmailVerified(member?.member?.id, !member?.member?.emailVerified)
                  }
                  disabled={!user.can_access_admin}
                />
                <p>Email Verfified</p>
              </div>
            </div>

            <fieldset
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                border: 0,
                padding: "1rem 0",
                margin: 0,
              }}
            >
              <div
                className="buttonContainer"
                style={{ margin: 0 }}
                onClick={() => (editEmailOpen ? handleSetEmailOpen(false) : handleSetEmailOpen(true))
                }
              >
                {editEmailOpen ? "Cancel" : "Edit Email"}
              </div>
              {editEmailOpen && (
                <button
                  className="buttonContainer"
                  style={{ margin: 0 }}
                  disabled={isLoading}
                  onClick={() => {
                    handleUpdateMember({ email });
                    handleSetEmailOpen(false);
                  }}
                >
                  Save
                </button>
              )}
              {!editEmailOpen && (
                <div
                  className="buttonContainer"
                  style={{ margin: 0 }}
                  onClick={() => setConfirmationModalOpen(true)}
                >
                  Delete
                </div>
              )}
            </fieldset>
          </div>
          <div style={{ marginTop: "1rem" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                marginBottom: "8px",
              }}
            >
              <button
                onClick={handleToggleAdminControls}
                style={{
                  background: "none",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  padding: "4px 8px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                {adminControlsExpanded ? "Collapse Admin Controls" : "Expand Admin Controls"}
              </button>
              <button
                onClick={handleToggleIntegrations}
                style={{
                  background: "none",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  padding: "4px 8px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                {integrationsExpanded ? "Collapse Integrations" : "Expand Integrations"}
              </button>
            </div>
            <div style={{ width: "60%" }}>
              {adminControlsExpanded && (
                <>
                  <h4 style={{ marginBottom: "0.5rem" }}>Feature Flags</h4>
                  <div
                    style={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      padding: "0.75rem",
                      backgroundColor: "#f9f9f9",
                    }}
                  >
                    <FormGroup row style={{ alignItems: "center" }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              mainTeam?.team
                                ? isTextFollowUp && mainTeam?.team?.textFollowUp
                                : isTextFollowUp
                            }
                            onChange={(e) => handleUpdateMember({ textFollowUp: e.target.checked })}
                            name="textFollowUp"
                            inputProps={{ "aria-label": "secondary checkbox" }}
                            disabled={
                              isCommonActionLoading
                              || (mainTeam?.team && !mainTeam?.team?.textFollowUp)
                            }
                          />
                        }
                        label={"Text Follow ups"}
                      />
                    </FormGroup>
                    <FormGroup row style={{ alignItems: "center" }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              mainTeam?.team
                                ? isEmailFollowUp && mainTeam?.team?.emailFollowUp
                                : isEmailFollowUp
                            }
                            onChange={(e) => handleUpdateMember({ emailFollowUp: e.target.checked })
                            }
                            name="emailFollowUp"
                            inputProps={{ "aria-label": "secondary checkbox" }}
                            disabled={
                              isCommonActionLoading
                              || (mainTeam?.team && !mainTeam?.team?.emailFollowUp)
                            }
                          />
                        }
                        label={"Email Follow ups"}
                      />
                    </FormGroup>
                    <FormGroup row style={{ alignItems: "center" }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isLcfEnabled}
                            onChange={async (e) => {
                              const isChecked = e.target.checked;
                              await Promise.all([
                                updateMemberLinksData(member?.member?.id, {
                                  leadCaptureMode: isChecked ? 1 : 0,
                                }),
                                handleUpdateMember({ exchange: isChecked ? "" : "0" }),
                              ]);
                            }}
                            name="lcfToggle"
                            inputProps={{ "aria-label": "lcf toggle" }}
                            disabled={isCommonActionLoading}
                          />
                        }
                        label={"Lead Capture Form"}
                      />
                    </FormGroup>
                    <MarketingEmailSettings member={member} setIsLoading={setIsLoading} />
                  </div>

                  <h4 style={{ marginTop: "1rem", marginBottom: "0.5rem" }}>Team Transfer</h4>
                  <div
                    style={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      padding: "0.75rem",
                      backgroundColor: "#f9f9f9",
                      marginBottom: "1rem",
                    }}
                  >
                    <form onSubmit={handleTeamTransferSubmit}>
                      <div style={{ marginBottom: "0.5rem" }}>
                        <TextField
                          fullWidth
                          label="New Team ID"
                          variant="outlined"
                          size="small"
                          value={newTeamId || ""}
                          onChange={(e) => setNewTeamId(e.target.value)}
                          disabled={isCommonActionLoading}
                          helperText="Enter team ID to transfer this member"
                        />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          marginTop: "0.5rem",
                        }}
                      >
                        <button
                          type="submit"
                          style={{
                            background: "#2196f3",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            padding: "6px 16px",
                            cursor: "pointer",
                            fontSize: "14px",
                          }}
                          disabled={isCommonActionLoading || !newTeamId}
                        >
                          Transfer Member
                        </button>
                      </div>
                    </form>
                  </div>
                  <h4 style={{ marginTop: "1rem", marginBottom: "0.5rem" }}>Member Devices</h4>
                  <div
                    style={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      padding: "0.75rem",
                      backgroundColor: "#f9f9f9",
                      marginBottom: "1rem",
                    }}
                  >
                    {memberDevices?.length > 0 ? (
                      <div style={{ marginBottom: "1rem" }}>
                        <h5 style={{ marginBottom: "0.5rem" }}>Registered Devices</h5>
                        <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                          {memberDevices?.map((device, index) => (
                            <div
                              key={device.id || index}
                              style={{
                                padding: "8px",
                                margin: "4px 0",
                                backgroundColor: "#f0f0f0",
                                borderRadius: "4px",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <div>
                                <p style={{ margin: "2px 0", fontSize: "14px" }}>
                                  <strong>ID:</strong> {device.name}
                                </p>
                                {device.nickname && (
                                  <p style={{ margin: "2px 0", fontSize: "14px" }}>
                                    <strong>Nickname:</strong> {device.nickname}
                                  </p>
                                )}
                                {device.createdAt && (
                                  <p style={{ margin: "2px 0", fontSize: "12px", color: "#666" }}>
                                    <strong>Registered:</strong>{" "}
                                    {new Date(device.createdAt).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p style={{ textAlign: "center", color: "#666", marginBottom: "1rem" }}>
                        No devices registered for this member
                      </p>
                    )}

                    <h5 style={{ marginBottom: "0.5rem" }}>Register New Device</h5>
                    <form onSubmit={handleRegisterDevice}>
                      <div style={{ marginBottom: "0.5rem" }}>
                        <TextField
                          fullWidth
                          label="Device ID"
                          name="id"
                          variant="outlined"
                          size="small"
                          value={deviceFormValues.id}
                          onChange={handleDeviceFormChange}
                          disabled={isLoading}
                          required
                        />
                      </div>
                      <div style={{ marginBottom: "0.5rem" }}>
                        <TextField
                          fullWidth
                          label="Device Nickname"
                          name="nickname"
                          variant="outlined"
                          size="small"
                          value={deviceFormValues.nickname}
                          onChange={handleDeviceFormChange}
                          disabled={isLoading}
                          required
                        />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          marginTop: "0.5rem",
                        }}
                      >
                        <button
                          type="submit"
                          style={{
                            background: "#2196f3",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            padding: "6px 16px",
                            cursor: "pointer",
                            fontSize: "14px",
                          }}
                          disabled={isLoading || !deviceFormValues.id || !deviceFormValues.nickname}
                        >
                          Register Device
                        </button>
                      </div>
                    </form>
                  </div>

                  <h4 style={{ marginBottom: "0.5rem" }}>Business Cards</h4>
                  <div
                    style={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      padding: "0.75rem",
                      backgroundColor: "#f9f9f9",
                      overflowY: "auto",
                      height: "fit-content",
                    }}
                  >
                    {linksData && linksData.length > 0 ? (
                      linksData.map((linkData, index) => (
                        <div key={linkData.id}>
                          <h5 style={{ marginTop: "0.5rem", marginBottom: "0.75rem" }}>
                            Card #{index + 1} (ID: {linkData.id})
                          </h5>
                          <form onSubmit={(e) => handleFormSubmitLinksData(e, linkData.id)}>
                            <div style={{ marginBottom: "0.5rem" }}>
                              <TextField
                                fullWidth
                                label="Save Contact Text"
                                variant="outlined"
                                size="small"
                                name="exchangeText"
                                value={linksDataFormValues[linkData.id]?.exchangeText || ""}
                                onChange={(e) => handleFormChange(e, linkData.id)}
                                disabled={isCommonActionLoading}
                              />
                            </div>
                            <div
                              style={{
                                marginBottom: "0.5rem",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <TextField
                                fullWidth
                                label="Assigned member"
                                variant="outlined"
                                size="small"
                                name="reassignMemberId"
                                helperText="Use this to reassign business card data to a new member"
                                value={linksDataFormValues[linkData.id]?.reassignMemberId || ""}
                                onChange={(e) => handleFormChange(e, linkData.id)}
                                disabled={isCommonActionLoading}
                              />
                            </div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                marginTop: "0.5rem",
                              }}
                            >
                              <button
                                type="submit"
                                style={{
                                  background: "#2196f3",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "4px",
                                  padding: "6px 16px",
                                  cursor: "pointer",
                                  fontSize: "14px",
                                }}
                                disabled={isCommonActionLoading}
                              >
                                Save
                              </button>
                            </div>
                          </form>
                        </div>
                      ))
                    ) : (
                      <p style={{ textAlign: "center", color: "#666" }}>
                        No business card data available
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
            {integrationsExpanded && (
              <IntegrationsSection
                integrationConnections={integrationConnections}
                crmExportLogs={crmExportLogs}
                isIntegrationsLoading={isIntegrationsLoading}
                isCrmExportLogsLoading={isCrmExportLogsLoading}
                onSearchCrmExportLogs={handleSearchCrmExportLogs}
                entityId={member?.member?.id}
              />
            )}
          </div>
        </div>
      </div>
      {member?.member?.memberTeams?.length > 0 && member?.member?.activeTeamsMember !== 0 ? (
        <h3>Team</h3>
      ) : (
        <div className="buttonContainer" onClick={(e) => startTeamsTrial(member?.member?.email)}>
          Create New Team
        </div>
      )}
      {member?.member?.activeTeamsMember !== 0
        && member?.member?.memberTeams?.map((memberTeam, index) => (
          <div>
            <div
              className="buttonContainer"
              onClick={(e) => props.openTeamFn(memberTeam?.team?.id)}
            >
              Open Team
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                border: "1px solid gray",
                padding: "1rem",
              }}
            >
              <div>
                <p>Team ID: {memberTeam?.team?.id}</p>
                <p>Is Admin: {memberTeam?.isAdmin ? "Yes" : "No"}</p>
              </div>
              <div
                style={{
                  borderRadius: "10px",
                  display: "flex",
                  gap: "1rem",
                  paddingTop: "1rem",
                }}
              >
                {user.can_access_admin ? (
                  memberTeam?.isAdmin ? (
                    <div
                      className="buttonContainerAlt"
                      onClick={
                        isLoading
                          ? null
                          : (e) => makeAdmin(member?.member?.id, memberTeam?.team?.id, false)
                      }
                    >
                      {isLoading ? (
                        <CircularProgress color="secondary" size={15} />
                      ) : (
                        "Remove Admin"
                      )}
                    </div>
                  ) : (
                    <div
                      className="buttonContainerAlt"
                      onClick={
                        isLoading
                          ? null
                          : (e) => makeAdmin(member?.member?.id, memberTeam?.team?.id, true)
                      }
                    >
                      {isLoading ? <CircularProgress color="secondary" size={15} /> : "Make Admin"}
                    </div>
                  )
                ) : null}
              </div>
            </div>
          </div>
        ))}

      <ConfirmationModal
        open={confirmationModalOpen}
        message="Are you sure you want to delete this member?"
        callback={async () => {
          await deleteMember(member?.member?.id, member?.member?.email);
        }}
        onClose={() => {
          setConfirmationModalOpen(false);
        }}
        onCloseParent={props.closeModal}
      />
    </div>
  );

  return (
    <Modal open={props.open} onClose={handleClose}>
      <div style={modalStyle} className={classes.paper}>
        {isLoading || isCommonActionLoading ? <CircularProgress /> : body}
      </div>
    </Modal>
  );
}
